import { type BaseState, EXCEPTION_KEYWORD, type ServiceAppState } from '@kc-console/context';
import { STATUS_CODE } from '@kc-console/core';

import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { getRequestUrlData } from '../../utils/getRequestUrlData';

import type { Guard } from '../../types';

/**
 * 사용자가 선택한 콘솔 버전과 BCS 의 BD 와 UR 간 패스 차이를 보정하여 진입 또는 리다이렉트를 결정하는 이니셜 가드
 * @description
 * - Local 개발 또는 beyond-console.* 또는 preview-console.* 도메인으로 접근 시, BD 에 대한 개발 또는 배포된 내용 확인 용도이기 때문에 그대로 진입 처리
 * - 사용자가 콘솔 버전을 UR 로 설정한 후, BCS 관련 패스로 진입하게 되면 패스를 보정하여 UR VM 또는 BM 으로 리다이렉트
 * - 사용자가 콘솔 버전을 BD 로 설정하거나 또는 한번도 설정 없이 초기 값인 BD 로 설정된 경우, VM 또는 BM 패스로 진입하게 되면 패스를 보정하여 BCS/VM 또는 BCS/BM 으로 리다이렉트
 * - BCS 가 CUTOVER 상태가 되면 해당 사용자는 무조건 BD 로 진입해야하기 때문에 해당 가드를 제거할 예정
 * @returns { Promise<GuardResponse> }
 */
export const determineConsoleVersionInitialGuard: Guard<ServiceAppState & BaseState> = async ({
  context,
  state,
}) => {
  try {
    const { resolvedUrl } = context;
    const { urlObj } = getRequestUrlData(context);
    const { origin } = urlObj;

    // Local 개발 또는 beyond-console.* 또는 preview-console.* 도메인으로 접근 시, BD 에 대한 개발 또는 배포된 내용 확인 용도이기 때문에 그대로 진입 처리
    const isByPass =
      process.env.NODE_ENV === 'development' ||
      origin.includes(EXCEPTION_KEYWORD.BEYOND_CONSOLE) ||
      origin.includes(EXCEPTION_KEYWORD.PREVIEW_CONSOLE);

    const nextObj = { status: STATUS_CODE.OK };

    const shouldRedirectToBDVersion =
      resolvedUrl.startsWith(EXCEPTION_KEYWORD.VIRTUAL_MACHINE) ||
      resolvedUrl.startsWith(EXCEPTION_KEYWORD.BARE_METAL_SERVER);

    if (!isByPass) {
      if (shouldRedirectToBDVersion) {
        // UR 패스인 /virtual-machine or /bare-metal-server 로 진입되는 경우.
        // BD 로의 패스 보정을 해서 보내줘야하기 때문에 해당 로직을 처리하기 위한 임시 구간
        return {
          redirect: {
            destination: `${origin}${EXCEPTION_KEYWORD.BEYOND_COMPUTE_SERVICE}${resolvedUrl}`,
            permanent: false,
          },
        };
      }
    }

    return nextObj;
  } catch (error) {
    return commonGuardErrorHandler({ context, state, error });
  }
};

determineConsoleVersionInitialGuard.displayName = 'determineConsoleVersionInitialGuard';
